/** Peças da ajuda: o conteúdo de um guia (tela, painel e impressão) e a capa impressa (D-064). */
import { DESCRICAO_PERFIS, hojeEmBrasilia, type Perfil } from '@metaepi/shared';
import { Info } from 'lucide-react';
import { dataBR } from '../pacientes/formatos';
import { TextoGuia } from './Texto';
import { ancoraSecao, guiaDoPerfil, type Guia, type SecaoGuia } from './tipos';

const quemFaz = (g: Guia) =>
  g.perfis === 'todos'
    ? 'todos os perfis'
    : g.perfis.map((p) => DESCRICAO_PERFIS[p].rotulo.toLowerCase()).join(', ');

export function ConteudoSecao({
  guia,
  secao,
  numero,
  Titulo = 'h2',
}: {
  guia: Guia;
  secao: SecaoGuia;
  numero?: number;
  Titulo?: 'h2' | 'h3';
}) {
  return (
    <section
      id={ancoraSecao(guia.id, secao.id)}
      aria-labelledby={`titulo-${ancoraSecao(guia.id, secao.id)}`}
      className="flex scroll-mt-24 flex-col gap-3 break-inside-avoid-page"
    >
      <Titulo
        id={`titulo-${ancoraSecao(guia.id, secao.id)}`}
        className={
          Titulo === 'h2' ? 'text-lg font-semibold text-texto' : 'font-semibold text-texto'
        }
      >
        {numero ? `${numero}. ` : ''}
        {secao.titulo}
      </Titulo>
      <ol className="flex list-decimal flex-col gap-2 pl-6 text-texto marker:text-texto-suave">
        {secao.passos.map((p, i) => (
          <li key={i}>
            <TextoGuia texto={p} />
          </li>
        ))}
      </ol>
      {secao.observacao && (
        <p className="flex gap-2 rounded-lg bg-info-fundo p-3 text-sm text-texto">
          <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden="true" />
          <span>
            <TextoGuia texto={secao.observacao} />
          </span>
        </p>
      )}
      {secao.imagem && (
        <figure className="flex flex-col gap-1">
          <img
            src={`/ajuda/${secao.imagem.arquivo}`}
            alt={secao.imagem.descricao}
            loading="lazy"
            className="w-full max-w-3xl rounded-lg border border-borda"
          />
          <figcaption className="text-xs text-texto-suave">{secao.imagem.descricao}</figcaption>
        </figure>
      )}
    </section>
  );
}

/** Corpo do guia: aviso de quem faz a tarefa, sumário e seções. */
export function ConteudoGuia({
  guia,
  perfil,
  Titulo = 'h2',
}: {
  guia: Guia;
  perfil?: Perfil;
  Titulo?: 'h2' | 'h3';
}) {
  return (
    <div className="flex flex-col gap-6">
      {perfil && !guiaDoPerfil(guia, perfil) && (
        <p className="rounded-lg border border-borda bg-superficie-2 p-3 text-sm text-texto">
          {`Esta tarefa é feita por: ${quemFaz(guia)}.`}
        </p>
      )}
      {guia.secoes.length > 1 && (
        <nav aria-label={`Seções de ${guia.titulo}`} className="print:hidden">
          <ol className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {guia.secoes.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${ancoraSecao(guia.id, s.id)}`}
                  className="text-primaria hover:underline"
                >{`${i + 1}. ${s.titulo}`}</a>
              </li>
            ))}
          </ol>
        </nav>
      )}
      {guia.secoes.map((s, i) => (
        <ConteudoSecao key={s.id} guia={guia} secao={s} numero={i + 1} Titulo={Titulo} />
      ))}
      <p className="text-xs text-texto-suave">{`Texto revisado em ${dataBR(guia.revisadoEm)}.`}</p>
    </div>
  );
}

/** Capa da impressão / PDF: só aparece no papel (D-064). */
export function CapaImpressao({
  titulo,
  resumo,
  perfil,
}: {
  titulo: string;
  resumo?: string;
  perfil?: Perfil;
}) {
  return (
    <div className="hidden flex-col gap-2 border-b border-borda pb-4 print:flex">
      <img src="/logo-metaepi.jpg" alt="MetaEpi" width={210} height={88} />
      <p className="text-2xl font-bold">{titulo}</p>
      {resumo && <p>{resumo}</p>}
      <p className="text-sm">
        {perfil ? `Perfil: ${DESCRICAO_PERFIS[perfil].rotulo} · ` : ''}
        {`Gerado em ${dataBR(hojeEmBrasilia())} a partir da ajuda do sistema.`}
      </p>
    </div>
  );
}
